// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { z } from "zod";

// Minutes since midnight; a full day is 1440 minutes.
export const DAY_MINUTES = 24 * 60;

export const SCHEDULE_LABELS = ["model", "human", "qr", "off"] as const;
export type ScheduleLabel = (typeof SCHEDULE_LABELS)[number];

export const SCHEDULE_STRATEGIES = ["every_hour", "no_of_images", "static", "strict"] as const;
export type ScheduleStrategy = (typeof SCHEDULE_STRATEGIES)[number];

const labelEnum = z.enum(SCHEDULE_LABELS);

// The base schedule — one discriminated union mirroring the (future) API payload.
export const scheduleSettingsSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("every_hour"),
    labelType: labelEnum,
    awakeTime: z.number().min(1).max(6),
  }),
  z.object({
    type: z.literal("no_of_images"),
    noOfImages: z.number().min(1).max(144),
    labelTypes: z.array(labelEnum).min(1),
    awakeTime: z.number().min(1).max(6),
  }),
  z.object({
    type: z.literal("static"),
    timeRanges: z.array(z.tuple([z.number(), z.number()])).max(144),
    labelTypes: z.array(labelEnum),
    awakeTime: z.number().min(1).max(6),
  }),
  z.object({
    type: z.literal("strict"),
    timeSlots: z.array(z.number()).max(288),
    labelTypes: z.array(labelEnum),
  }),
]);
export type ScheduleSettings = z.infer<typeof scheduleSettingsSchema>;

export const scheduleExceptionSchema = z.object({
  id: z.string(),
  // The API serialises absent optionals as null, so accept null and normalise to undefined.
  name: z
    .string()
    .nullish()
    .transform((v) => v ?? undefined),
  tickets: z
    .array(z.string())
    .nullish()
    .transform((v) => v ?? undefined),
  expression: z
    .string()
    .nullish()
    .transform((v) => v ?? undefined),
  definition: scheduleSettingsSchema,
});
export type ScheduleException = z.infer<typeof scheduleExceptionSchema>;

export const storeScheduleSchema = z.object({
  base: scheduleSettingsSchema,
  exceptions: z.array(scheduleExceptionSchema),
});
export type StoreSchedule = z.infer<typeof storeScheduleSchema>;

// The view-model the timeline renders. `start === end` for `strict` point slots.
export interface TimelineSegment {
  start: number;
  end: number;
  label: ScheduleLabel;
}
